<template>
  <div>
    <base-content-block :need-back="false">
      <template #action v-if="training">
        <router-link :to="'/courses/edit/' + training.course.id" class="btn btn-primary">{{
          $t('src/pages/trainingview.8697')
        }}</router-link>
      </template>
      <template #title v-if="training">
        <div class="ml-2">
          <span class="card-header__item">{{ $t('src/pages/trainingview.4039', { '1': trainingId }) }}</span>
          <span class="card-header__item">{{ trainingStartDate }}</span>
          <span class="card-header__item">
            <status-title :status="training.status" :type="'session'" :is-badge="true"></status-title>
          </span>
        </div>
      </template>
      <template v-if="isActive" #headerRight>
        <button
          class="btn btn-primary btn-with-icon"
          :class="{ invisible: isNeuroAdmin || isReadonly }"
          @click="showChat"
        >
          <span :class="isShowChat ? 'icon icon-close icon_small' : 'icon icon-message'">
            <div v-if="isNewMessage" class="message-new"></div>
          </span>
          {{ isShowChat ? $t('button.closeChat') : $t('button.chat') }}
        </button>
      </template>
      <template v-else-if="hasECG || training" #headerRight>
        <BaseActionMenu>
          <template #actions>
            <div v-if="hasECG">
              <download-e-c-g
                :ecg="training.ecg_record"
                :training-id="training.id"
                :patient="training.course.patient"
                :date-start="training.date_start"
                :course-id="training.course.id"
              ></download-e-c-g>
            </div>
            <div v-if="training && events && is6MWT">
              <protocol
                :training="training"
                :patient="training.course.patient"
                :rhythmogram="rhythmogram"
                :events="localEvents"
                :stepRate="stepRate"
                :markers="markers"
                :signalsCount="signalInfo"
              ></protocol>
            </div>
            <div v-else-if="training && !is6MWT">
              <protocol-session
                v-if="localeRU"
                :training="training"
                :patient="training.course.patient"
                :step-rate="stepRate"
                :rhythmogram="rhythmogram"
                :events="localEvents"
              >
              </protocol-session>
              <protocol-session-en
                v-else
                :training="training"
                :patient="training.course.patient"
                :step-rate="stepRate"
                :rhythmogram="rhythmogram"
                :events="localEvents"
              >
              </protocol-session-en>
            </div>
          </template>
        </BaseActionMenu>
      </template>
      <template v-else #headerRight
        ><span style="width: 150px"></span
      ></template>
      <template #content>
        <div v-if="loading">
          <base-spinner></base-spinner>
        </div>
        <div v-else-if="training">
          <div class="term-row">
            <div class="term-col term-col-full">
              <div class="term">
                <div class="term-item term-item">{{ $t('general.physician') }}:</div>
                <div class="term-value">{{ doctorName }}</div>
              </div>
            </div>
            <div class="term-col term-col-full">
              <div class="term">
                <div class="term-item term-item">{{ $t('entity.patient') }}:</div>
                <router-link
                  :to="{ path: `/patients/edit/${training.course.patient.id}` }"
                  class="term-value text-secondary"
                  >{{ patientName }} {{ agePatientStr }}</router-link
                >
              </div>
            </div>
          </div>
        </div>
        <chat
          v-if="isFirstOpen && isActive"
          v-show="isShowChat"
          :isShow="isShowChat"
          :activeChat="training.course_id"
          :activeTrainings="activeTrain"
          :is-show="isShowChat"
          @close:chat="showChat"
          @change:haveNewMessage="updateHaveNewMessage"
        ></chat>
      </template>
    </base-content-block>

    <div :class="{ 'd-flex justify-content-between mt-3 mb-2': isTrainDone }">
      <section class="card" :class="{ 'term-col-50 term-margin': isTrainDone }">
        <div v-if="loading">
          <base-spinner></base-spinner>
        </div>
        <div v-else-if="training">
          <div class="card-header card-header-light">
            <h4 class="title">{{ $t('src/pages/trainingview.4254') }}</h4>
          </div>
          <div class="card-body">
            <training-parameters-form
              ref="trainparams"
              :training="training"
              :saving="saving"
              @save="saveTraining"
            ></training-parameters-form>
          </div>
        </div>
      </section>

      <section class="card term-col-50" v-if="isTrainDone">
        <div class="term-row term-row-form card-body">
          <svg-icon
            v-if="isCourseCreator && !isEditMode"
            class="ml-2 mt-1 position-absolute"
            style="right: 20"
            :name="'edit_pen_inside_the_square'"
            :width="20"
            :height="20"
            @click:icon="openTrainInfoEditMode"
          ></svg-icon>
          <div class="term">
            <div class="term-item term-item-lg">{{ $t('src/pages/trainingview.51') }}</div>
            <div v-if="!isEditable" class="term-value">{{ specialistInterpretation }}</div>
            <div v-else class="term-value">
              <base-textarea
                name="interpretation"
                ref="traininterpret"
                v-model="training.specialist_interpretation"
              ></base-textarea>
            </div>
          </div>

          <div class="term">
            <div class="term-item term-item-lg">{{ $t('src/pages/trainingview.3562') }}</div>
            <div v-if="!isEditable" class="term-value">{{ basicRhythm }}</div>
            <div v-else class="term-value">
              <base-textarea
                name="basicRhythm"
                ref="basicRhythm"
                v-model="training.basic_rhythm"
              ></base-textarea>
            </div>
          </div>

          <div class="term">
            <div class="term-item term-item-lg">{{ $t('src/pages/trainingview.4406') }}:</div>
            <div v-if="!isEditable" class="term-value">{{ diagnosis }}</div>
            <div v-else class="term-value">
              <base-textarea name="diagnosis" ref="diagnosis" v-model="training.diagnosis"></base-textarea>
            </div>
          </div>

          <div class="term">
            <div class="term-item term-item-lg">{{ $t('src/pages/trainingview.4900') }}</div>
            <div v-if="!isEditable" class="term-value">{{ trainingType }}</div>
            <div v-else class="term-value">
              <div>
                <div class="custom-control custom-radio d-inline-block mr-4">
                  <input
                    class="custom-control-input"
                    type="radio"
                    name="exampleRadios"
                    id="standard"
                    :value="0"
                    v-model="localTrainingType"
                  />
                  <label class="custom-control-label" for="standard">{{
                    $t('src/pages/trainingview.1920')
                  }}</label>
                </div>
                <div class="custom-control custom-radio d-inline-block">
                  <input
                    class="custom-control-input"
                    type="radio"
                    name="exampleRadios"
                    id="6mwt"
                    :value="1"
                    v-model="localTrainingType"
                  />
                  <label class="custom-control-label" for="6mwt">{{
                    $t('src/pages/trainingview.wt6m')
                  }}</label>
                </div>
              </div>
            </div>
          </div>
          <button v-if="isEditable" type="button" class="btn btn-primary mt-3" @click="saveTrainingData">
            <span v-if="saving" class="spinner-border spinner-border-sm"></span>{{ $t('button.save') }}
          </button>
        </div>
      </section>
    </div>

    <section class="card mt-3 mb-2" v-if="isMissed">
      <div class="card-header card-header-light">
        <h4 class="card-header__title">{{ $t('src/pages/trainingview.3537') }}</h4>
      </div>
      <div class="card-body">
        <template v-if="hasMessage">
          {{ training.comment }}
        </template>
        <div class="alert alert-danger" v-else>
          <p>{{ $t('src/pages/trainingview.5212') }}</p>
          <p>
            {{ $t('src/pages/trainingview.9289') }}<strong>{{ training.course.patient.phone }}</strong>
          </p>
        </div>
      </div>
    </section>

    <section class="card mt-3 mb-2" v-if="isCanceled">
      <div class="card-header card-header-light">
        <h4 class="card-header__title">{{ $t('src/pages/trainingview.6457') }}</h4>
      </div>
      <div class="card-body">
        <template v-if="hasMessage">
          {{ training.comment }}
        </template>
        <div class="alert alert-danger" v-else>
          <p>{{ $t('src/pages/trainingview.3892') }}</p>
          <p>
            {{ $t('src/pages/trainingview.9289') }}<strong>{{ training.course.patient.phone }}</strong>
          </p>
        </div>
      </div>
    </section>
    <training-results
      ref="trainingResult"
      v-if="!loading && (isDone || isActive)"
      :training="training"
      :rhythmogram="rhythmogram"
      :events="events"
      :geoLocationPoints="geoLocationPoints"
      :step-rate="stepRate"
      :current-h-r="currentHR"
      :breath="breath"
      :diffTimeComputerAndServer="diffTimeComputerAndServer"
      @get:signalInfo="getsignalInfo"
      @set:localEvents="setLocalEvents"
    ></training-results>
  </div>
</template>

<script>
import http from '@/modules/http.js'
import { fullName } from '@/mixins/full-name.js'
import { status, eventTypes, DATE_FORMAT } from '@/main.config.js'
import { subscribeMobileEvents } from '@/mixins/subscribe-mobile-events.js'
import { contentTpl } from '@/mixins/content-tpl.js'
import { trainingDate } from '@/mixins/training-date.js'
import { savedMessage } from '@/mixins/saved-message'
import { userRights } from '@/mixins/user-rights.js'
import { trainingItem } from '@/mixins/training-item.js'
import { TRAINING_TYPE } from '@/main.config'
import { systemUnavailable } from '@/mixins/system-unavailable.js'
import { patientInfo } from '@/mixins/patient-info'
import declension from '@/modules/declension'

import('@/styles/components/terms.less')

export default {
  name: 'TrainingView',
  components: {
    TrainingResults: () => import('@/components/Training/TrainingResults.vue'),
    TrainingParametersForm: () => import('@/components/Training/TrainingParametersForm.vue'),
    StatusTitle: () => import('@/components/StatusTitle.vue'),
    DownloadECG: () => import('@/components/Training/DownloadECG.vue'),
    BaseSpinner: () => import('@/base/BaseSpinner.vue'),
    BaseTextarea: () => import('@/base/BaseTextarea.vue'),
    Chat: () => import('@/components/Chat.vue'),
    Protocol: () => import('@/components/Protocol.vue'),
    ProtocolSession: () => import('@/components/ProtocolSession.vue'),
    ProtocolSessionEn: () => import('@/components/ProtocolSessionEn.vue'),
    SvgIcon: () => import('@/components/SvgIcon.vue'),
    BaseActionMenu: () => import('@/base/BaseActionMenu.vue')
  },
  mixins: [
    fullName,
    subscribeMobileEvents,
    contentTpl,
    trainingDate,
    savedMessage,
    userRights,
    trainingItem,
    systemUnavailable,
    patientInfo
  ],
  provide() {
    return {
      $isEditableSession: () => this.isEditable
    }
  },
  data: () => ({
    training: null,
    loading: true,
    saving: false,
    rhythmogram: null,
    stepRate: null,
    events: null,
    geoLocationPoints: null,
    breath: null,
    currentHR: null,
    currentBF: null,
    currentSR: null,
    eventsBeginIndexes: {},
    isShowChat: false,
    isFirstOpen: false,
    haveNewMessage: false,
    break: null,
    localTrainingType: null,
    signalInfo: null,
    localEvents: null,
    lastDataTime: null,
    diffTimeComputerAndServer: null,
    isEditMode: false
  }),
  computed: {
    localeRU() {
      return this.$i18n.locale === 'ru'
    },
    trainingId() {
      return this.$route.params.id
    },
    course() {
      return this.training.course
    },
    trainingStartDate() {
      return this.getTrainingFormattedStartDate(this.training)
    },
    hasECG() {
      if (!this.training) {
        return false
      }
      return !!this.training.ecg_record
    },
    patientName() {
      return this.getFullName(this.training.course.patient)
    },
    doctorName() {
      return this.getFullName(this.training.course.doctor)
    },
    hasMessage() {
      return !!this.training.comment
    },
    isMissed() {
      if (!this.training) {
        return false
      }
      return this.training.status === status.missed
    },
    isDone() {
      if (!this.training) {
        return false
      }
      return this.training.status === status.done
    },
    isActive() {
      if (!this.training) {
        return false
      }
      return this.training.status === status.active
    },
    isCanceled() {
      if (!this.training) {
        return false
      }
      return this.training.status === status.canceled
    },
    hrValuesLength() {
      if (!this.rhythmogram || !this.rhythmogram.timeValues) {
        return null
      }
      return this.rhythmogram.timeValues.length
    },
    hrLastTimeValue() {
      if (!this.hrValuesLength) {
        return null
      }
      return this.rhythmogram.timeValues[this.hrValuesLength]
    },
    specialistInterpretation() {
      if (this.training.specialist_interpretation) return this.training.specialist_interpretation
      else return '—'
    },
    basicRhythm() {
      if (this.training.basic_rhythm) return this.training.basic_rhythm
      else return '—'
    },
    diagnosis() {
      if (this.training.diagnosis) return this.training.diagnosis
      else return '—'
    },
    trainingType() {
      if (this.training.training_type === TRAINING_TYPE.base) {
        return this.$t('src/pages/trainingview.1920')
      } else if (this.training.training_type === TRAINING_TYPE.sixmwt) {
        return '6WMT'
      } else return '—'
    },
    isEditable() {
      return this.isCourseCreator && this.isEditMode
    },
    isTrainDone() {
      return this.isDone
    },
    isNewMessage() {
      return this.haveNewMessage
    },
    markers() {
      if (
        window.sessionStorage.getItem(`markStart${this.trainingId}`) &&
        window.sessionStorage.getItem(`markEnd${this.trainingId}`)
      ) {
        return {
          start: window.sessionStorage.getItem(`markStart${this.trainingId}`),
          end: window.sessionStorage.getItem(`markEnd${this.trainingId}`)
        }
      }
      let startSec = 0

      let evStart = this.events.find(item => item.episodeType === 'eventStartWalking')

      if (evStart) {
        startSec = evStart.beginTime
      } else {
        if (this.stepRate) {
          let idxStart
          for (let i = 0; i < this.stepRate.rateValues.length; i++) {
            if (this.stepRate.rateValues[i] !== 0) {
              idxStart = i
              break
            }
          }
          if (idxStart) {
            startSec = this.stepRate.timeValues[idxStart]
          }
        }
      }

      let endSec = 0
      let evStop = this.events.find(item => item.episodeType === 'eventStopWalking')
      if (evStop) {
        endSec = evStop.beginTime > startSec + 360 ? startSec + 360 : evStop.beginTime
      } else {
        endSec = startSec + 360
      }

      window.sessionStorage.setItem(`markStart${this.trainingId}`, startSec)
      window.sessionStorage.setItem(`markEnd${this.trainingId}`, endSec)
      return {
        start: startSec,
        end: endSec
      }
    },
    activeTrain() {
      let activeTrains = []
      activeTrains.push(this.training)
      return activeTrains
    },
    agePatientStr() {
      let age = this.getAgeAtSpecificDate(this.training.date_start, this.training.course.patient.birth_date)
      return age
        ? this.$i18n.locale === 'ru'
          ? `(${age} ${declension(age, [`год`, `года`, `лет`])})`
          : `(${age} years)`
        : ''
    }
  },
  mounted() {
    this.loadTraining()
  },
  updated() {},
  methods: {
    loadTraining() {
      http
        .get('/trainings/' + this.trainingId, {
          headers: {
            redirect404: true
          }
        })
        .then(response => {
          this.training = response.data
          let front = this.moment().utc()
          let back = this.moment(this.training.currentDateTime).utc()
          this.diffTimeComputerAndServer = front.diff(back, 'seconds')
          this.localTrainingType = this.training.training_type
        })
        .then(() => {
          this.checkStatusForTracking()
          if (this.isActive) {
            this.getCourse()
          }
        })

        .finally(() => {
          this.loading = false
        })
    },
    getCourse() {
      http.get('/courses/' + this.training.course_id).then(response => {
        this.haveNewMessage = response.data.unread_messages
      })
    },
    checkStatusForTracking() {
      //Если тренировка выполнена или пропущена, то нет смысла подписываться на онлайн события, устанавливаем ритмограмму и события и уходим
      if (this.isDone || this.isMissed || this.isCanceled) {
        let parsedRhytm = JSON.parse(this.training.rhytmogram)
        if (parsedRhytm) {
          this.rhythmogram = {
            timeValues: parsedRhytm.timeValues,
            hrValues: parsedRhytm.hrValues.map(hr => Math.round(hr))
          }
        }

        this.events = JSON.parse(this.training.events) || []
        this.localEvents = this.events
        this.stepRate = JSON.parse(this.training.step_rates)
        if (this.training.breath) {
          this.breath = JSON.parse(this.training.breath)
        }
        if (this.training.filtered_geolocation_points) {
          this.geoLocationPoints = {
            timeValues: this.training.filtered_geolocation_points.map(point => {
              return point.time
            }),
            geoValues: this.training.filtered_geolocation_points.map(point => {
              return [point.latitude, point.longitude]
            })
          }
        }
        return
      }
      //если тренировка идет, то нужно получить все ранее полученные данные
      if (this.isActive) {
        this.getPreviousData()
      }

      this.canSubscribe = true
    },
    saveTraining(training) {
      this.saving = true

      http
        .patch('/trainings/' + this.trainingId, training)
        .then(response => {
          this.training = Object.assign(this.training, response.data)
          this.$emit('saved:data')
        })
        .catch(error => {
          if (error.response.data.message === 'The system has expired') {
            this.$emit('system:unavailable')
          }
          console.log(error)
        })
        .finally(() => {
          this.saving = false
        })
    },
    getPreviousRhytmogram() {
      return new Promise(resolve => {
        http
          .get('/ongoing-training/' + this.trainingId, {
            params: {
              type: 'hr'
            }
          })
          .then(response => {
            let rhythmogram = { timeValues: [], hrValues: [] }
            if (response.data.total > 0) {
              rhythmogram = response.data.data.reduce(
                function(obj, hrCurrent) {
                  obj.timeValues.push(parseInt(hrCurrent.time, 10))
                  obj.hrValues.push(parseInt(hrCurrent.value, 10))

                  return obj
                },
                { timeValues: [], hrValues: [] }
              )
              this.currentHR = +rhythmogram.hrValues[this.rhythmogram.hrValues.length - 1]
            }
            return rhythmogram
          })
          .then(data => {
            resolve(data)
          })
      })
    },
    getPreviousEvents() {
      return new Promise(resolve => {
        http
          .get('/ongoing-training/' + this.trainingId, {
            params: {
              type: 'ce'
            }
          })
          .then(response => response.data)
          .then(data => {
            resolve(data)
          })
      })
    },
    getPreviousBreath() {
      return new Promise(resolve => {
        http
          .get('/ongoing-training/' + this.trainingId, {
            params: {
              type: 'bf'
            }
          })
          .then(response => {
            let breath = { timeValues: [], brValues: [] }
            if (response.data.total > 0) {
              breath = response.data.data.reduce(
                function(obj, bfCurrent) {
                  obj.timeValues.push(parseInt(bfCurrent.time, 10))
                  obj.brValues.push(+bfCurrent.value)
                  return obj
                },
                { timeValues: [], brValues: [] }
              )
              this.currentBF = breath.brValues[this.breath.brValues.length - 1]
            }
            return breath
          })
          .then(data => {
            resolve(data)
          })
      })
    },
    getPreviousSteps() {
      return new Promise(resolve => {
        http
          .get('/ongoing-training/' + this.trainingId, {
            params: {
              type: 'sr'
            }
          })
          .then(response => {
            let stepRate = { timeValues: [], rateValues: [] }
            if (response.data.total > 0) {
              stepRate = response.data.data.reduce(
                function(obj, srCurrent) {
                  obj.timeValues.push(parseInt(srCurrent.time, 10))
                  obj.rateValues.push(+srCurrent.value)
                  return obj
                },
                { timeValues: [], rateValues: [] }
              )
              this.currentSR = stepRate.rateValues[this.stepRate.rateValues.length - 1]
            }
            return stepRate
          })
          .then(data => {
            resolve(data)
          })
      })
    },
    getPreviousDisconnection() {
      return new Promise(resolve => {
        http
          .get('/ongoing-training/' + this.trainingId, {
            params: {
              type: 'br'
            }
          })
          .then(response => response.data)
          .then(data => {
            resolve(data)
          })
      })
    },
    getPreviousReconnection() {
      return new Promise(resolve => {
        http
          .get('/ongoing-training/' + this.trainingId, {
            params: {
              type: 'rc'
            }
          })
          .then(response => response.data)
          .then(data => {
            resolve(data)
          })
      })
    },
    getPreviousGeoLocationPoints() {
      return new Promise(resolve => {
        http
          .get('/ongoing-training/' + this.trainingId, {
            params: {
              type: 'geo'
            }
          })
          .then(response => {
            let geoLocationPoints = { timeValues: [], geoValues: [] }
            if (response.data.total > 0) {
              geoLocationPoints = response.data.data.reduce(
                (obj, geoCurrent) => {
                  obj.timeValues.push(parseInt(geoCurrent.time, 10))
                  obj.geoValues.push([geoCurrent.latitude, geoCurrent.longitude])
                  return obj
                },
                { timeValues: [], geoValues: [] }
              )
            }
            return geoLocationPoints
          })
          .then(data => {
            resolve(data)
          })
      })
    },
    getPreviousData() {
      this.loading = true
      this.rhythmogram = { timeValues: [], hrValues: [] }
      this.events = []
      this.breath = { timeValues: [], brValues: [] }
      this.stepRate = { timeValues: [], rateValues: [] }
      this.geoLocationPoints = { timeValues: [], geoValues: [] }

      Promise.all([
        this.getPreviousRhytmogram(),
        this.getPreviousEvents(),
        this.getPreviousBreath(),
        this.getPreviousSteps(),
        this.getPreviousDisconnection(),
        this.getPreviousReconnection(),
        this.getPreviousGeoLocationPoints()
      ])
        .then(responses => {
          this.rhythmogram = responses[0]
          this.breath = responses[2]
          this.stepRate = responses[3]
          this.geoLocationPoints = responses[6]

          //обработка кардиособытий
          if (responses[1].total > 0) {
            responses[1].data.forEach(event => {
              if (event.type === eventTypes.cardioEvent) {
                this.eventProcessing(event)
              }
            })
          }
          //обработка событий потери связи
          if (responses[4].total > 0) {
            responses[4].data.forEach(event => {
              this.eventProcessing(event)
            })
          }
          //обработка событий восстановления связи
          if (responses[5].total > 0) {
            responses[5].data.forEach(event => {
              this.eventProcessing(event)
            })
          }
        })
        .then((this.loading = false))
    },
    changeDataByEvent(event) {
      if (+this.training.id !== +event.tr_id) {
        return
      }

      //если получили данные для открытой тренировки, тип события не начало тренировки, а статус открытой трени не активен, то перезагрузим ее сначала
      if (this.training.status !== status.active && event.type !== eventTypes.startTraining) {
        this.loadTraining()
        return
      }

      this.lastDataTime = event.type === 'SR' && event.value === 0 ? this.lastDataTime : event.time
      localStorage.setItem(`lastDataTime${this.training.id}`, this.lastDataTime)
      this.eventProcessing(event)
    },
    eventProcessing(event) {
      //обработка полученной информации
      switch (event.type) {
        case eventTypes.startTraining:
          this.updateStartTraining(event.ts)
          return
        case eventTypes.changeHR:
          if (this.hrLastTimeValue > Math.round(event.time)) {
            return
          }
          this.rhythmogram.timeValues.push(Math.round(event.time))
          this.rhythmogram.hrValues.push(Math.round(event.value))
          this.currentHR = +event.value
          return
        case eventTypes.changeBF:
          this.breath.timeValues.push(event.time)
          this.breath.brValues.push(event.value)
          this.currentBF = event.value
          return
        case eventTypes.changeSR:
          this.stepRate.timeValues.push(event.time)
          this.stepRate.rateValues.push(event.value)
          this.currentSR = event.value
          return
        case eventTypes.cardioEvent:
          this.addCardioEvent(event)
          return
        case eventTypes.changeCoordinate:
          this.addGeoPoint(event)
          return
        case eventTypes.break:
          this.addConnectionEvent(event)
          return
        case eventTypes.reconnected:
          this.addConnectionEvent(event)
          return
        case eventTypes.stopTraining:
          this.canSubscribe = false
          this.updateEndTraining(event.ts)
          return
      }
    },
    addCardioEvent(event) {
      let eventObj = {
        episodeType: event.episodeType,
        value: event.value
      }
      let time = Math.round(event.time)

      if (event.evt === 'B') {
        this.eventsBeginIndexes[event.episodeType] = this.events.length
        eventObj['beginTime'] = time
        eventObj['endTime'] = time + 1 //добавлю секунду как дату окончания, а потом она изменится
      } else if (event.evt === 'E') {
        let idx = this.eventsBeginIndexes[event.episodeType]
        if (!this.events[idx]) {
          return
        }
        this.events[idx]['endTime'] = time
        delete this.eventsBeginIndexes[event.episodeType]
        return
      } else {
        eventObj['beginTime'] = time
      }

      this.events.push(eventObj)
    },
    addGeoPoint(event) {
      //вставляем в массив точки в порядке возрастания времени
      let idx = this.geoLocationPoints.timeValues.length
      while (idx > 0 && this.geoLocationPoints.timeValues[idx - 1] > event.time) {
        idx--
      }
      this.geoLocationPoints.timeValues.splice(idx, 0, event.time)
      this.geoLocationPoints.geoValues.splice(idx, 0, [event.latitude, event.longitude])
    },
    addConnectionEvent(event) {
      let eventObj = {
        episodeType: 'break',
        value: event.value,
        beginTime: Math.round(event.time)
      }
      if (event.type == 'BR') {
        eventObj['episodeType'] = 'break'
      } else if (event.type == 'RC') {
        eventObj['episodeType'] = 'reconnected'
      }
      this.events.push(eventObj)
    },
    updateStartTraining(/*ts*/) {
      this.rhythmogram = { timeValues: [], hrValues: [] }
      this.events = []
      this.training.status = status.active
      //TODO: в будущем я обязательно поставлю время с приложения, а пока локальное
      // this.training.date_start = this.moment(ts).format(DATE_FORMAT.serverFull)
      this.training.date_start = this.moment.utc().format(DATE_FORMAT.serverFull)
    },
    updateEndTraining(/*ts*/) {
      //окончание тренировки по времени сервера
      this.training.status = status.done
      //TODO: в будущем я обязательно поставлю время с приложения, а пока локальное
      // this.training.date_end = this.moment(ts).format(DATE_FORMAT.serverFull)
      this.training.date_end = this.moment.utc().format(DATE_FORMAT.serverFull)
      this.currentHR = null
      this.currentBF = null

      setTimeout(() => {
        //полная загрузка данных завершенной трени с сервера
        this.loadTraining()
      }, 1000)
    },
    saveTrainingData() {
      this.training.training_type = this.localTrainingType
      this.saveTraining(this.training)
      this.isEditMode = false
    },
    showChat() {
      this.isShowChat = !this.isShowChat
      this.isFirstOpen = true
      this.haveNewMessage = false
    },
    updateHaveNewMessage() {
      if (!this.isShowChat) {
        this.haveNewMessage = true
      }
    },
    getsignalInfo(info) {
      this.signalInfo = info.length
    },
    setLocalEvents(events) {
      this.localEvents = events
    },
    openTrainInfoEditMode() {
      this.isEditMode = true
    }
  }
}
</script>
